import { isoDay } from '../lib/format';
import { C, EASE_SPRING, ink } from '../lib/theme';
import { IconChevronLeft, IconChevronRight } from './icons';

function monthRange(d: Date): { from: string; to: string } {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from: isoDay(first), to: isoDay(last) };
}

function monthLabel(d: Date): string {
  const s = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Seletor de mês — mais prático que o `PeriodSelector` genérico pra quem só
 * quer "o mês vigente, ou o anterior/seguinte": ‹ › andam mês a mês sem abrir
 * calendário nenhum, e o mês atual é calculado toda vez a partir de `new
 * Date()` (nunca hardcoded), então amanhã virando setembro já reflete sem
 * precisar de deploy. Sem período selecionado (`from` undefined) mostra
 * "Tudo" — clicar em ‹ › a partir daí assume o mês vigente como ponto de
 * partida.
 */
export function MonthPicker({ from, onChange }: {
  from?: string;
  onChange: (from?: string, to?: string) => void;
}) {
  const anchor = from ? new Date(`${from}T00:00:00`) : new Date();
  const isAll = !from;
  const isCurrentMonth = !isAll && monthRange(new Date()).from === monthRange(anchor).from;

  function shift(delta: number) {
    const next = new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
    const r = monthRange(next);
    onChange(r.from, r.to);
  }

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 13,
      background: ink(0.05), border: `1px solid ${ink(0.1)}`,
    }}>
      <button
        type="button" onClick={() => shift(-1)} title="Mês anterior" className="ko-ghost"
        style={{
          width: 30, height: 30, borderRadius: 10, border: 'none', background: 'transparent',
          color: ink(0.6), cursor: 'pointer', font: 'inherit', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      ><IconChevronLeft size={15} /></button>

      <span style={{
        minWidth: 148, textAlign: 'center', fontSize: 13, fontWeight: 750, color: isAll ? ink(0.55) : C.fg,
        padding: '0 4px',
      }}>{isAll ? 'Tudo (histórico completo)' : monthLabel(anchor)}</span>

      <button
        type="button" onClick={() => shift(1)} title="Próximo mês" className="ko-ghost"
        style={{
          width: 30, height: 30, borderRadius: 10, border: 'none', background: 'transparent',
          color: ink(0.6), cursor: 'pointer', font: 'inherit', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      ><IconChevronRight size={15} /></button>

      <div style={{ width: 1, height: 18, background: ink(0.1), margin: '0 4px' }} />

      {!isCurrentMonth && (
        <button
          type="button"
          onClick={() => { const r = monthRange(new Date()); onChange(r.from, r.to); }}
          style={{
            border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 700,
            padding: '6px 11px', borderRadius: 9, whiteSpace: 'nowrap',
            background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`, color: '#fff',
            transition: `transform 0.2s ${EASE_SPRING}`,
          }}
        >Mês atual</button>
      )}
      {isCurrentMonth && (
        <span style={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
          color: C.accent, padding: '6px 9px',
        }}>Agora</span>
      )}
      {!isAll && (
        <button
          type="button" onClick={() => onChange(undefined, undefined)} title="Ver todo o histórico"
          style={{
            border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 650,
            padding: '6px 9px', borderRadius: 9, background: 'transparent', color: ink(0.42),
            textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >Tudo</button>
      )}
    </div>
  );
}
